import React from "react";

const DEFAULT_SCROLL_FADE_DISTANCE = 18;
const EMPTY_SCROLL_EDGES = { top: false, bottom: false, hasOverflow: false };

const getScrollEdges = (list) => {
  if (!list) return EMPTY_SCROLL_EDGES;

  const hasOverflow = list.scrollHeight > list.clientHeight + 1;
  const top = hasOverflow && list.scrollTop > 1;
  const bottom =
    hasOverflow && list.scrollTop + list.clientHeight < list.scrollHeight - 1;

  return { top, bottom, hasOverflow };
};

const getScrollFadeMask = (edges, fadeDistance) => {
  if (!edges.top && !edges.bottom) return "none";

  const topFadeEnd = edges.top ? `${fadeDistance}px` : "0px";
  const bottomFadeStart = edges.bottom
    ? `calc(100% - ${fadeDistance}px)`
    : "100%";

  return `linear-gradient(to bottom, transparent 0, #000 ${topFadeEnd}, #000 ${bottomFadeStart}, transparent 100%)`;
};

const setEdgesIfChanged = (setEdges, nextEdges) => {
  setEdges((currentEdges) =>
    currentEdges.top === nextEdges.top &&
    currentEdges.bottom === nextEdges.bottom &&
    currentEdges.hasOverflow === nextEdges.hasOverflow
      ? currentEdges
      : nextEdges,
  );
};

export default function useScrollFade({
  disabled = false,
  fadeDistance = DEFAULT_SCROLL_FADE_DISTANCE,
  observeContent = true,
} = {}) {
  const scrollRef = React.useRef(null);
  const [scrollEdges, setScrollEdges] = React.useState(EMPTY_SCROLL_EDGES);

  const updateScrollState = React.useCallback(() => {
    if (disabled) {
      setEdgesIfChanged(setScrollEdges, EMPTY_SCROLL_EDGES);
      return EMPTY_SCROLL_EDGES;
    }

    const nextEdges = getScrollEdges(scrollRef.current);
    setEdgesIfChanged(setScrollEdges, nextEdges);
    return nextEdges;
  }, [disabled]);

  React.useEffect(() => {
    updateScrollState();
  });

  React.useEffect(() => {
    if (disabled) {
      setEdgesIfChanged(setScrollEdges, EMPTY_SCROLL_EDGES);
      return undefined;
    }

    const list = scrollRef.current;
    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateScrollState)
        : null;

    if (list && resizeObserver) {
      resizeObserver.observe(list);
      if (observeContent && list.firstElementChild) {
        resizeObserver.observe(list.firstElementChild);
      }
    }

    window.addEventListener("resize", updateScrollState);
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateScrollState);
    };
  }, [disabled, observeContent, updateScrollState]);

  const maskImage = getScrollFadeMask(scrollEdges, fadeDistance);
  const scrollFadeSx = React.useMemo(
    () => ({
      WebkitMaskImage: maskImage,
      maskImage,
      WebkitMaskSize: "100% 100%",
      maskSize: "100% 100%",
    }),
    [maskImage],
  );

  return {
    scrollRef,
    scrollFadeSx,
    scrollEdges,
    hasOverflow: scrollEdges.hasOverflow,
    onScroll: updateScrollState,
    updateScrollState,
  };
}
